import { CheckCircledIcon, CopyIcon } from '@radix-ui/react-icons'
import { cva, type VariantProps } from 'class-variance-authority'
import React, { type ButtonHTMLAttributes, ComponentPropsWithRef } from 'react'

import { Grid } from '@/components/ui/Box'
import { useCopy } from '@/hooks/useCopy'
import { cn } from '@/lib/utils'

import { Loading } from './Icon'

const buttonVariants = cva(
  cn(
    'flex items-center justify-center space-x-1 px-2 border rounded-sm',
    'transition-all cursor-pointer hover:opacity-90 disabled:opacity-80 disabled:backdrop-blur-2xl disabled:cursor-not-allowed'
  ),
  {
    variants: {
      variant: {
        primary: 'bg-primary border-primary text-primary-foreground',
        destructive: 'bg-destructive text-primary-foreground border-destructive',
        secondary: 'bg-background-secondary',
        tertiary: 'bg-background-tertiary border-background-tertiary text-text-secondary',
        fourth: 'bg-background-fourth text-text-secondary border-background-fourth'
      },
      size: {
        xxs: 'h-6 text-xs space-x-1 rounded-sm',
        xs: 'h-7 text-sm space-x-1 rounded',
        sm: 'h-8 text-sm rounded-lg',
        md: 'h-10 text-base rounded-xl',
        lg: 'h-12 text-base rounded-2xl'
      },
      ghost: {
        true: 'bg-transparent border-none'
      },
      outline: {
        true: 'bg-transparent'
      }
    },
    compoundVariants: [
      {
        outline: true,
        variant: 'primary',
        className: 'text-primary bg-background-primary'
      },
      {
        outline: true,
        variant: 'fourth',
        className: 'text-background-fourth'
      },
      {
        ghost: true,
        variant: 'destructive',
        className: 'text-destructive'
      },
      {
        ghost: true,
        variant: 'primary',
        className: 'text-primary'
      }
    ],
    defaultVariants: {
      size: 'md'
    }
  }
)

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, className, ghost, outline, size, variant, loading, ...rest } = props
  return (
    <button
      type="button"
      ref={ref}
      className={cn(buttonVariants({ ghost, outline, size, variant, className }))}
      {...rest}
    >
      {loading && <Loading className="size-4" />}
      {typeof children === 'string' ? <span>{children}</span> : children}
    </button>
  )
})

interface BaseOption {
  label: React.ReactNode
  value: number | string
}

export interface ButtonRadioProps<D extends BaseOption = BaseOption> extends React.HTMLAttributes<HTMLDivElement> {
  options: D[]
  value?: D['value']
  optionProps?: ButtonProps
  onValueChange?: (value: D['value'], option: D) => void
}

export const ButtonRadio = <D extends BaseOption = BaseOption>({
  optionProps,
  options,
  // value: currentValue,
  onValueChange,
  ...rest
}: ButtonRadioProps<D>) => {
  return (
    <Grid {...rest}>
      {options.map((option) => (
        <Button
          key={option.value}
          // outline={currentValue === option.value}
          // variant={currentValue === option.value ? 'primary' : 'secondary'}
          size="md"
          type="button"
          {...optionProps}
          onClick={(ev) => {
            onValueChange?.(option.value, option)
            optionProps?.onClick?.(ev)
          }}
        >
          {option.label}
        </Button>
      ))}
    </Grid>
  )
}

export const SelectorButton = React.forwardRef<React.ComponentRef<typeof Button>, ButtonProps>(
  ({ children, ...rest }, ref) => (
    <Button ref={ref} variant="secondary" size="sm" {...rest}>
      {children}
      {/* <Icon.SelectorArrow /> */}
    </Button>
  )
)

export const CopyButton: React.FC<ComponentPropsWithRef<typeof Button> & { text?: string }> = ({
  children,
  className,
  text,
  ...rest
}) => {
  const { buttonRef, isCopied } = useCopy(text || '')

  return (
    <Button
      ref={buttonRef}
      ghost
      {...rest}
      disabled={isCopied}
      className={cn('size-auto !p-0 disabled:opacity-100', className)}
    >
      {(children ?? isCopied) ? <CheckCircledIcon className="text-success" /> : <CopyIcon className="text-primary" />}
    </Button>
  )
}
